import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI, clearCache } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearCache();
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      clearCache();

      const response = await authAPI.login(credentials);
      const { user, accessToken } = response.data.data;
      // accessToken stored in localStorage for Authorization header
      // refreshToken is in an HttpOnly cookie set by the server — never touches localStorage

      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      clearCache();

      setUser(user);
      setIsAuthenticated(true);

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please try again.',
        code: error.response?.data?.code,
        email: error.response?.data?.email,
        // Surface providers list when backend returns
        // SOCIAL_ACCOUNT_NO_PASSWORD so the Login page can render
        // a "Continue with Google" / "Continue with Microsoft" CTA
        // inline instead of a generic error toast.
        providers: error.response?.data?.providers
      };
    }
  };

  // const register = async (userData) => {
  //   try {
  //     const response = await authAPI.register(userData);
  //     const { user, accessToken, refreshToken } = response.data.data;

  //     localStorage.setItem('token', accessToken);
  //     localStorage.setItem('refreshToken', refreshToken);
  //     localStorage.setItem('user', JSON.stringify(user));

  //     setUser(user);
  //     setIsAuthenticated(true);

  //     return { success: true };
  //   } catch (error) {
  //     return {
  //       success: false,
  //       message: error.response?.data?.message || 'Registration failed. Please try again.'
  //     };
  //   }
  // };

  const register = async (userData) => {
    try {
      await authAPI.register(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };
  
  const logout = async () => {
    // Clear client state synchronously so the UI flips to logged-out
    // immediately. The backend cookie-clear call is fire-and-forget —
    // a slow server (cold start, network blip) shouldn't make the
    // user wait 2-3 seconds staring at a transitioning page.
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearCache();
    setUser(null);
    setIsAuthenticated(false);
    // Best-effort server-side refreshToken cookie clear; safe to fail.
    authAPI.logout().catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout API call failed (non-fatal):', error);
      }
    });
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const response = await userAPI.getProfile();
      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  // Auto-correct timezone for newly signed-up users. The OAuth controller
  // hardcodes 'Australia/Sydney' as the default for all new social-login
  // users — that's wrong for the ~70% of new signups who aren't in
  // Australia. If the browser disagrees with what we have stored AND the
  // account is fresh AND the stored value is still the AU default, push
  // the browser value to the server. Long-term users who are travelling
  // are left alone (they'd have a non-default timezone or older account).
  useEffect(() => {
    if (!user || !user.timezone) return;
    if (user.timezone !== 'Australia/Sydney') return;
    let browserTz;
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_) {
      return;
    }
    if (!browserTz || browserTz === 'Australia/Sydney') return;
    // Only auto-correct for accounts younger than 24h, so we don't
    // surprise a long-time AU user who's traveling.
    const created = user.createdAt ? new Date(user.createdAt).getTime() : NaN;
    if (!Number.isFinite(created)) return;
    if (Date.now() - created > 24 * 60 * 60 * 1000) return;

    // Normalise the legacy 'Asia/Calcutta' to the modern 'Asia/Kolkata' so
    // downstream Indian-pricing checks see a single canonical value (we
    // accept both in the detection logic but storing one form is cleaner).
    const normalisedTz = browserTz === 'Asia/Calcutta' ? 'Asia/Kolkata' : browserTz;

    userAPI.updateProfile({ timezone: normalisedTz })
      .then(() => refreshUser())
      .catch(() => { /* silent — non-critical */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.timezone, user?.createdAt]);

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};